import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import { listSellos, deleteSello } from "../services/sellos";
import buildSellosTableData from "./datasellos";
import { listGrupos } from "../services/grupos";

const toArray = (v) => (Array.isArray(v) ? v : []);

/* ---------------- BÚSQUEDA ---------------- */
const CONTAINS_FIELDS = ["name", "nombre"];
const EXACT_FIELDS = ["group_id", "id", "_id"];

const SEARCH_PARAM_MAP = {
  nombre: "name",
  name: "name",
  grupo: "group_id",
  group_id: "group_id",
  id: "id",
  _id: "_id",
};

// Parser búsqueda → params API
function buildSearchParams(input = "") {
  const s = String(input || "").trim();
  const params = {};
  if (!s) return params;

  const tokens = s.match(/\S+:"[^"]+"|\S+:\S+|".+?"|\S+/g) || [];
  const pairs = [];
  const free = [];

  for (const tok of tokens) {
    const m = tok.match(/^([^:]+):(.+)$/);
    if (m) {
      const rawKey = m[1].toLowerCase().trim();
      let rawVal = m[2].trim();
      if (rawVal.startsWith('"') && rawVal.endsWith('"')) rawVal = rawVal.slice(1, -1);
      const key = SEARCH_PARAM_MAP[rawKey] || rawKey;
      pairs.push([key, rawVal]);
    } else {
      free.push(tok.replace(/^"|"$/g, ""));
    }
  }

  for (const [key, value] of pairs) {
    if (EXACT_FIELDS.includes(key) || /^\d+$/.test(value)) {
      params[key] = value;
      continue;
    }
    if (CONTAINS_FIELDS.includes(key)) {
      params[`${key}[$contains]`] = value;
    } else {
      params["name[$contains]"] = value;
    }
  }

  if (free.length) {
    const q = free.join(" ").replace(/\s+/g, " ").trim();
    params["name[$contains]"] = q;
  }

  return params;
}

/**
 * Mapa entre nombre de columna (UI) y campo real de BD
 * - "sello"  → name
 * - "creado" → created_at
 */
const SORT_FIELD_MAP = {
  sello: "name",
  creado: "created_at",
};

export default function Sellos() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // mapa idGrupo -> nombreGrupo
  const [groupMap, setGroupMap] = useState({});

  // 🔽 estado de ordenamiento (por defecto: creado desc → últimos primero)
  const [sort, setSort] = useState({
    uiField: "creado",      // nombre de la columna en la UI
    apiField: "created_at", // campo real del backend
    direction: "desc",      // "asc" | "desc"
  });

  const openSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // cargar grupos una sola vez para poder mostrar nombre en el listado
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        const data = await listGrupos({
          __limit: 1000,
          __offset: 0,
          __sort: { name: 1 },
        });
        if (cancel) return;
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.page)
          ? data.page
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.results)
          ? data.results
          : [];
        const map = {};
        arr.forEach((g) => {
          const id = g.id ?? g._id;
          if (id == null) return;
          const name = g.name ?? g.nombre ?? String(id);
          map[String(id)] = name;
        });
        setGroupMap(map);
      } catch {
        // si falla, simplemente mostraremos el id como fallback
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  // fetch con paginación + búsqueda + orden
  const fetchList = async ({
    pageIndex = page,
    pageSize = rowsPerPage,
    currentSearch = search,
    sortField = sort.apiField,
    sortDirection = sort.direction,
  } = {}) => {
    setLoading(true);
    setErr("");

    try {
      const limit = pageSize;
      const offset = pageIndex * pageSize;

      const searchParams = buildSearchParams(currentSearch);

      const sortParam =
        sortField != null
          ? { [sortField]: sortDirection === "asc" ? 1 : -1 }
          : null;

      const res = await listSellos({
        __limit: limit,
        __offset: offset,
        ...(sortParam ? { __sort: sortParam } : {}),
        ...searchParams,
      });

      setItems(res.rows || []);
      setTotal(Number(res.total || 0));
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  // clic en encabezado de columna (sello / creado)
  const handleSortChange = (columnName) => {
    const apiField = SORT_FIELD_MAP[columnName] || columnName;

    setPage(0);

    setSort((prev) => {
      let direction = "asc";

      if (prev.uiField === columnName && prev.direction === "asc") {
        direction = "desc";
      }

      const next = {
        uiField: columnName,
        apiField,
        direction,
      };

      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: next.apiField,
        sortDirection: next.direction,
      });

      return next;
    });
  };

  // carga inicial → ordenado por fecha desc
  useEffect(() => {
    fetchList({
      pageIndex: 0,
      pageSize: rowsPerPage,
      currentSearch: search,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // búsqueda con debounce (manteniendo el orden actual)
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(0);
      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rowsPerPage]);

  // construir filas de tabla
  const { columns, rows } = useMemo(
    () =>
      buildSellosTableData(toArray(items), (it) => {
        const id = it.id ?? it._id;
        const nombre = it.name ?? it.nombre ?? "-";

        const groupIds = Array.isArray(it.groups) && it.groups.length
          ? it.groups
              .map((g) =>
                typeof g === "object" && g !== null
                  ? g.id ?? g._id
                  : g
              )
              .filter(
                (id) =>
                  id !== null &&
                  id !== undefined &&
                  id !== ""
              )
              .map(String)
          : it.group_id != null
          ? [String(it.group_id)]
          : it.groupId != null
          ? [String(it.groupId)]
          : [];

        const groupLabels = groupIds.map((groupId) => {
          const groupObject = Array.isArray(it.groups)
            ? it.groups.find(
                (g) =>
                  typeof g === "object" &&
                  g !== null &&
                  String(g.id ?? g._id) === String(groupId)
              )
            : null;

          return (
            groupObject?.name ??
            groupObject?.nombre ??
            groupMap[String(groupId)] ??
            `Grupo ${groupId}`
          );
        });

        const groupLabel =
          groupLabels.length > 0
            ? groupLabels.join(", ")
            : "-";

        return {
          id,
          nombre,
          groupIds,
          groupLabel,
          creado: it.created_at ?? it.createdAt,
          onEdit: () => navigate(`/catalogos/sellos/${id}/editar`),
          onDelete: () => setPending({ id, nombre }),
        };
      }),
    [items, navigate, groupMap]
  );

  const confirmDelete = async () => {
    if (!pending) return;

    try {
      await deleteSello(pending.id);
      openSnack(`Sello eliminado: ${pending.nombre}`, "success");

      await fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || String(e);
      setErr(`No se pudo eliminar: ${msg}`);
      openSnack(`No se pudo eliminar: ${msg}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Sellos"
        search={search}
        onSearchChange={setSearch}
        addText="Agregar"
        onAdd={() => navigate("/catalogos/sellos/nuevo")}
        columns={columns}
        rows={rows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        sortField={sort.uiField}
        sortDirection={sort.direction}
        onSortChange={handleSortChange}
        paginationProps={
          !loading && {
            count: total,
            page,
            rowsPerPage,
            onPageChange: (_e, newPage) => {
              setPage(newPage);
              fetchList({
                pageIndex: newPage,
                pageSize: rowsPerPage,
                currentSearch: search,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            onRowsPerPageChange: (e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
              fetchList({
                pageIndex: 0,
                pageSize: newSize,
                currentSearch: search,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar sello"
        description={`¿Seguro que quieres eliminar "${pending?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}
